import { useCallback, useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

const PAGE_SIZE = 100;

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatMoney(cents, currency) {
  if (typeof cents !== "number") return "—";
  const amt = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(amt);
  } catch {
    return `$${amt.toFixed(2)}`;
  }
}

function StatusPill({ status }) {
  const cls =
    status === "active" || status === "trialing" || status === "paid"
      ? "bg-emerald-100 text-emerald-700"
      : status === "past_due" || status === "payment_failed"
      ? "bg-amber-100 text-amber-800"
      : status === "canceled" || status === "uncollectible" || status === "void"
      ? "bg-neutral-200 text-neutral-700"
      : "bg-neutral-100 text-neutral-600";
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {status || "—"}
    </span>
  );
}

function Pagination({ page, totalPages, total, onChange, loading }) {
  if (!total) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-black/5 text-[11.5px] text-neutral-600 bg-neutral-50">
      <div>
        Showing <span className="tabular font-medium">{(page - 1) * PAGE_SIZE + 1}</span>
        –<span className="tabular font-medium">{Math.min(page * PAGE_SIZE, total)}</span>
        {" "}of <span className="tabular font-medium">{total.toLocaleString()}</span>
        {loading && <span className="ml-2 text-neutral-400">refreshing…</span>}
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onChange(1)}
          disabled={page <= 1 || loading}
          className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40"
        >
          « First
        </button>
        <button
          type="button"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1 || loading}
          className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40"
        >
          ‹ Prev
        </button>
        <span className="px-2 tabular">
          Page {page} / {Math.max(1, totalPages)}
        </span>
        <button
          type="button"
          onClick={() => onChange(Math.min(totalPages || 1, page + 1))}
          disabled={page >= totalPages || loading}
          className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40"
        >
          Next ›
        </button>
        <button
          type="button"
          onClick={() => onChange(totalPages || 1)}
          disabled={page >= totalPages || loading}
          className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40"
        >
          Last »
        </button>
      </div>
    </div>
  );
}

function useList(path) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchPage = useCallback(async (p) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(path, { params: { page: p, page_size: PAGE_SIZE } });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.total_pages || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  return { items, page, setPage, total, totalPages, loading, err };
}

export default function AdminBilling() {
  const subs = useList("/admin/billing/subscriptions");
  const pays = useList("/admin/billing/payments");

  return (
    <AdminLayout activeTab="billing" title="Billing">
      <div className="space-y-8">
        {/* SUBSCRIBERS */}
        <section data-testid="admin-billing-subscribers">
          <h2 className="font-display text-lg font-bold tracking-tight mb-3">Subscribers</h2>
          {subs.err && <div className="text-[13px] text-red-600 mb-3">{subs.err}</div>}
          <div className="card overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="text-left text-neutral-500 bg-neutral-50">
                <tr>
                  <th className="py-2 px-3 font-medium">User</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 px-3 font-medium">Started</th>
                  <th className="py-2 px-3 font-medium">Renews / ends</th>
                  <th className="py-2 px-3 font-medium">Cancels?</th>
                  <th className="py-2 px-3 font-medium">Stripe</th>
                </tr>
              </thead>
              <tbody>
                {subs.items.length === 0 && !subs.loading && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-neutral-500">
                      No subscriptions yet.
                    </td>
                  </tr>
                )}
                {subs.items.map((s) => (
                  <tr key={s.stripe_subscription_id} className="border-t border-black/5">
                    <td className="py-2 px-3">
                      <div className="font-medium">{s.email || s.user_id}</div>
                      {s.name && <div className="text-[11.5px] text-neutral-500">{s.name}</div>}
                    </td>
                    <td className="py-2 px-3"><StatusPill status={s.status} /></td>
                    <td className="py-2 px-3">{formatDate(s.created_at)}</td>
                    <td className="py-2 px-3">{formatDate(s.current_period_end)}</td>
                    <td className="py-2 px-3">
                      {s.cancel_at_period_end ? (
                        <span className="text-amber-700 text-[11.5px] font-medium">yes</span>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <span className="text-[11.5px] text-neutral-500 font-mono">
                        {(s.stripe_subscription_id || "").slice(0, 14)}…
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={subs.page}
              totalPages={subs.totalPages}
              total={subs.total}
              loading={subs.loading}
              onChange={subs.setPage}
            />
          </div>
        </section>

        {/* PAYMENTS */}
        <section data-testid="admin-billing-payments">
          <h2 className="font-display text-lg font-bold tracking-tight mb-3">Payments</h2>
          {pays.err && <div className="text-[13px] text-red-600 mb-3">{pays.err}</div>}
          <div className="card overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="text-left text-neutral-500 bg-neutral-50">
                <tr>
                  <th className="py-2 px-3 font-medium">Date</th>
                  <th className="py-2 px-3 font-medium">User</th>
                  <th className="py-2 px-3 font-medium">Amount</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 px-3 font-medium">Period</th>
                  <th className="py-2 px-3 font-medium text-right">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {pays.items.length === 0 && !pays.loading && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-neutral-500">
                      No payments yet.
                    </td>
                  </tr>
                )}
                {pays.items.map((p) => (
                  <tr key={p.stripe_invoice_id} className="border-t border-black/5">
                    <td className="py-2 px-3">{formatDate(p.paid_at || p.created_at)}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium">{p.email || p.user_id}</div>
                    </td>
                    <td className="py-2 px-3">{formatMoney(p.amount_cents, p.currency)}</td>
                    <td className="py-2 px-3"><StatusPill status={p.status} /></td>
                    <td className="py-2 px-3 text-[11.5px] text-neutral-500">
                      {formatDate(p.period_start)} → {formatDate(p.period_end)}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {p.hosted_invoice_url ? (
                        <a
                          href={p.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[12px] text-neutral-600 hover:text-black inline-flex items-center gap-1"
                        >
                          View <ExternalLink size={11} />
                        </a>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={pays.page}
              totalPages={pays.totalPages}
              total={pays.total}
              loading={pays.loading}
              onChange={pays.setPage}
            />
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
